/**
 * ConfigService.js contains all of the service functions used for the overall
 * and profile configurations.
 *
 * @endpoints /configuration/:
 * 		overall/retrieve - retrieve the overall config
 * 		profile/retrieve - retrieve the profile config
 * 		profile/delete - delete the specified profile
 * 		profile/create - create or update a profile
 * 		profile/deploy - deploy a non-master profile
 *		profile/masterDefaultDeploy - deploy a master default profile
 *
 * @author Sophia Frankel <sophia.frankel@quavo.com>
 */

import API from '@/services/APIService';

export default {
	name: 'ConfigService',
	/**
	 * Retrieve the overall config based on the provided tenantId
	 * @param {String} tenantId - parameter for the request body
	 * @param {*} dispatch - dispatch a notification in the event of an error
	 * @returns an object containing the overall configuration data
	 */
	async getOverallConfig(tenantId, dispatch) {
		return await API.call('/configuration/overall/retrieve', {
			"tenantId": tenantId,
			"clientId": "Default"
		}).catch(error => {
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
			});
		});
	},
	/**
	 * Retrieve the profile config based on the provided tenantId and profileId
	 * @param {Object} payload - parameters for the request body
	 * @param {*} dispatch - dispatch a notification in the event of an error
	 * @returns an object containing the profile configuration data
	 */
	getProfile(payload, dispatch) {
		return API.call('/configuration/profile/retrieve', {
			"tenantId": payload.tenantId,
			"clientId": "Default",
			"profileId": payload.profileId
		}).catch(error => {
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
			})
		});
	},
	/**
	 * Delete a profile based on the provided tenantId and profileId
	 * @param {Object} payload - parameters for the request body
	 * @param {*} dispatch - dispatch a notification in the event of an error
	 * @returns an object containing a status message
	 */
	deleteProfile(payload, dispatch) {
		return API.call('/configuration/profile/delete', {
			"tenantId": payload.tenantId,
			"clientId": "Default",
			"profileId": payload.profileId
		}).catch(error => {
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
			})
		});
	},
	/**
	 * Update a profile based on the provided payload
	 * @param {Object} payload - parameters for the request body
	 * @param {*} dispatch - dispatch a notification in the event of an error
	 * @returns an object containing a status message and the profileId of the updated profile
	 */
	updateProfile(payload, dispatch) {
		return API.call('/configuration/profile/create', {
			"tenantId": payload.profile.TenantId,
			"clientId": payload.profile.clientId,
			"profileId": payload.profile.profileId,
			"profileLabel": payload.profile.profileLabel,
			"profileDescription": payload.profile.profileDescription,
			"isActive": payload.profile.isActive,
			"masterDefault": payload.profile.masterDefault,
			"deployStartDateTime": payload.profile.deployStartDateTime,
			"deployEndDateTime": payload.profile.deployEndDateTime,
			"initialDecisionConfiguration": payload.profile.initialDecisionConfiguration,
			"overallDecisionConfiguration": payload.profile.overallDecisionConfiguration,
			"auditMessages": payload.auditMessages

		}).catch((error) => {
			dispatch("notifications/addNotification", {
				status: 'error',
				message: error.response.data.error,
			})
		});
	},
	/**
	 * Retrieve and make a copy of a profile based on the provided payload
	 * @param {Object} payload - parameters for the request body
	 * @param {*} dispatch - dispatch a notification in the event of an error
	 * @returns an object containing a status message and the profileId of the new profile
	 */
	copyProfile(payload, dispatch) {
		return this.getProfile(payload, dispatch).then(response => {
			return API.call('/configuration/profile/create', {
				"tenantId": payload.activeTenantId,
				"clientId": response.data.clientId,
				"profileLabel": payload.name,
				"profileDescription": response.data.profileDescription,
				"isActive": response.data.isActive,
				"masterDefault": false,
				"deployStartDateTime": response.data.deployStartDateTime,
				"deployEndDateTime": response.data.deployEndDateTime,
				"initialDecisionConfiguration": response.data.initialDecisionConfiguration,
				"overallDecisionConfiguration": response.data.overallDecisionConfiguration
			}).catch(error => {
				dispatch("notifications/addNotification", {
					status: 'error',
					message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
				})
			});
		});
	},
	/**
	 * Deploy a profile based on the provided profile object
	 * @param {Object} profile - profile object containing the request body parameters
	 * @param {*} dispatch - dispatch a notification in the event of an error
	 * @returns an object containing a status message and the profileId of the deployed profile
	 */
	deployProfile(profile, dispatch) {
		return API.call('/configuration/profile/deploy', {
			"tenantId": profile.TenantId,
			"clientId": profile.clientId,
			"profileId": profile.profileId,
		}).catch(error => {
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
			})
		});
	},
	/**
	 * Deploy the defualt master profile based on the provided profile object
	 * @param {Object} profile - profile object containing the request body parameters
	 * @param {*} dispatch - dispatch a notification in the event of an error
	 * @returns an object containing a status message and the profileId of the deployed profile
	 */
	deployDefaultMasterProfile(profile, dispatch) {
		return API.call('/configuration/profile/masterDefaultDeploy', {
			"tenantId": profile.TenantId,
			"clientId": profile.clientId,
			"profileId": profile.profileId,
		}).catch(error => {
			dispatch("notifications/addNotification", {
				status: 'error',
				message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
			})
		});
	},
	/**
	 * Retrieve and rename a profile based on the provided payload
	 * @param {Object} payload - parameters for the request body
	 * @param {*} dispatch - dispatch a notification in the event of an error
	 * @returns an object containing a status message and the profileId of the renamed profile
	 */
	renameProfile(payload, dispatch) {
		return this.getProfile(payload, dispatch).then(response => {
			return API.call('/configuration/profile/create', {
				"tenantId": response.data.TenantId,
				"clientId": response.data.clientId,
				"profileId": response.data.profileId,
				"profileLabel": payload.name,
				"profileDescription": response.data.profileDescription,
				"isActive": response.data.isActive,
				"masterDefault": response.data.masterDefault,
				"deployStartDateTime": response.data.deployStartDateTime,
				"deployEndDateTime": response.data.deployEndDateTime,
				"initialDecisionConfiguration": response.data.initialDecisionConfiguration,
				"overallDecisionConfiguration": response.data.overallDecisionConfiguration
			}).catch(error => {
				dispatch("notifications/addNotification", {
					status: 'error',
					message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
				})
			});
		});
	},
	/**
	 * Retrieve and activate/inactivate a profile based on the provided payload
	 * @param {Object} payload - parameters for the request body
	 * @param {*} dispatch - dispatch a notification in the event of an error
	 * @returns an object containing a status message and the profileId of the activated/inactivated profile
	 */
	activateProfile(payload, dispatch) {
		return this.getProfile(payload, dispatch).then(response => {
			return API.call('/configuration/profile/create', {
				"tenantId": response.data.TenantId,
				"clientId": response.data.clientId,
				"profileId": response.data.profileId,
				"profileLabel": response.data.profileLabel,
				"profileDescription": response.data.profileDescription,
				"isActive": payload.isActive,
				"masterDefault": response.data.masterDefault,
				"deployStartDateTime": response.data.deployStartDateTime,
				"deployEndDateTime": response.data.deployEndDateTime,
				"initialDecisionConfiguration": response.data.initialDecisionConfiguration,
				"overallDecisionConfiguration": response.data.overallDecisionConfiguration
			}).catch(error => {
				dispatch("notifications/addNotification", {
					status: 'error',
					message: 'A ' + error.message + ' has occurred. Please refresh the page or try again later.',
				})
			});
		});
	}
};